import { Link, useLocation } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { NavCollapsible, NavItem, NavLink, type NavGroup } from './types'
import { cn } from '@/lib/utils'

export function NavGroup({ title, items }: NavGroup) {
  const { state } = useSidebar()
  const href = useLocation({ select: (location) => location.href })

  return (
    <SidebarGroup>
      {title && (
        <SidebarGroupLabel className="text-xs font-semibold text-sidebar-muted uppercase tracking-wider px-4 mb-2">
          {title}
        </SidebarGroupLabel>
      )}
      <SidebarMenu className="space-y-0">
        {items.map((item) => {
          const key = `${item.title}-${item.url}`

          // Skip items that shouldn't be shown based on showIf condition
          if (item.showIf && !item.showIf()) {
            return null;
          }

          if (!item.items)
            return <SidebarMenuLink key={key} item={item} href={href} />

          if (state === 'collapsed')
            return (
              <SidebarMenuCollapsedDropdown key={key} item={item} href={href} />
            )

          return <SidebarMenuCollapsible key={key} item={item} href={href} />
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}

const SidebarMenuLink = ({ item, href }: { item: NavLink; href: string }) => {
  const { setOpenMobile } = useSidebar()
  const isActive = checkIsActive(href, item)
  const isRTL = typeof document !== 'undefined' && document?.dir === 'rtl'

  // Handler to prevent navigation if already on the same page
  const handleClick = (e: React.MouseEvent) => {
    if (isActive) {
      e.preventDefault()
    }
    setOpenMobile(false)
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={item.title}
        className={cn(
          "flex items-center gap-3 px-4 py-5 rounded-lg transition-colors duration-200 group",
          isActive
            ? "bg-secondary/20 text-white shadow-inner"
            : "text-blue-100 hover:bg-white/10 hover:text-white",
          // Add border for active state
          isActive && (isRTL ? "border-r-4 border-secondary" : "border-l-4 border-secondary")
        )}
      >
        <Link to={item.url} onClick={handleClick}>
          {item.icon && (
            <item.icon
              className={cn(
                "h-5 w-5 transition-colors",
                isActive
                  ? "text-secondary"
                  : "text-current group-hover:text-secondary"
              )}
            />
          )}
          <span className="font-medium">{item.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

const SidebarMenuCollapsible = ({
  item,
  href,
}: {
  item: NavCollapsible
  href: string
}) => {
  const { setOpenMobile } = useSidebar()
  const isActive = checkIsActive(href, item, true)
  const isRTL = typeof document !== 'undefined' && document?.dir === 'rtl'

  return (
    <Collapsible
      asChild
      defaultOpen={isActive}
      className='group/collapsible'
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            className={cn(
              "flex items-center gap-3 px-4 py-5 rounded-lg transition-colors duration-200 group",
              isActive
                ? "bg-secondary/20 text-white shadow-inner"
                : "text-blue-100 hover:bg-white/10 hover:text-white",
              isActive && (isRTL ? "border-r-4 border-secondary" : "border-l-4 border-secondary")
            )}
          >
            {item.icon && (
              <item.icon
                className={cn(
                  "h-5 w-5 transition-colors",
                  isActive ? "text-secondary" : "group-hover:text-secondary"
                )}
              />
            )}
            <span className="font-medium">{item.title}</span>
            <ChevronRight className='ms-auto h-4 w-4 transition-transform duration-300 group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180 rtl:group-data-[state=open]/collapsible:rotate-[270deg]' />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className='CollapsibleContent'>
          <SidebarMenuSub className="border-s border-white/20 ms-6 ps-3 mt-1">
            {item.items.map((subItem) => {
              // Skip items that shouldn't be shown based on showIf condition
              if (subItem.showIf && !subItem.showIf()) {
                return null;
              }

              const isSubActive = checkIsActive(href, subItem)
              const handleClick = (e: React.MouseEvent) => {
                if (isSubActive) {
                  e.preventDefault()
                }
                setOpenMobile(false)
              }
              return (
                <SidebarMenuSubItem key={subItem.title}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isSubActive}
                    className={cn(
                      "px-3 py-2 rounded-lg transition-colors",
                      isSubActive
                        ? "text-white bg-white/10"
                        : "text-blue-200 hover:text-white hover:bg-white/5"
                    )}
                  >
                    <Link to={subItem.url} onClick={handleClick}>
                      {subItem.icon && <subItem.icon className="h-4 w-4" />}
                      <span>{subItem.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              )
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

const SidebarMenuCollapsedDropdown = ({
  item,
  href,
}: {
  item: NavCollapsible
  href: string
}) => {
  const isActive = checkIsActive(href, item)
  const isRTL = typeof document !== 'undefined' && document?.dir === 'rtl'

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={isActive}
            className={cn(
              "flex items-center gap-3 px-4 py-4 rounded-lg transition-colors group",
              isActive
                ? "bg-secondary/20 text-white shadow-inner"
                : "text-blue-100 hover:bg-white/10 hover:text-white",
              isActive && (isRTL ? "border-r-4 border-secondary" : "border-l-4 border-secondary")
            )}
          >
            {item.icon && (
              <item.icon
                className={cn(
                  "h-5 w-5",
                  isActive ? "text-secondary" : "group-hover:text-secondary"
                )}
              />
            )}
            <span className="font-medium">{item.title}</span>
            <ChevronRight className='ms-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180 rtl:group-data-[state=open]/collapsible:rotate-[270deg]' />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={typeof document !== 'undefined' && document?.dir === 'rtl' ? 'left' : 'right'}
          align='start'
          sideOffset={4}
          className="min-w-48 bg-primary text-white border-blue-800"
        >
          <DropdownMenuLabel>
            {item.title}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-blue-800" />
          {item.items.map((sub) => {
            // Skip items that shouldn't be shown based on showIf condition
            if (sub.showIf && !sub.showIf()) {
              return null;
            }

            const isSubActive = checkIsActive(href, sub)
            const handleClick = (e: React.MouseEvent) => {
              if (isSubActive) {
                e.preventDefault()
              }
            }
            return (
              <DropdownMenuItem key={`${sub.title}-${sub.url}`} asChild className="focus:bg-white/10 focus:text-white">
                <Link
                  to={sub.url}
                  className={`${isSubActive ? 'bg-white/10' : ''}`}
                  onClick={handleClick}
                >
                  {sub.icon && <sub.icon className="h-4 w-4 me-2" />}
                  <span className='max-w-52 text-wrap'>{sub.title}</span>
                </Link>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}

function checkIsActive(href: string, item: NavItem, mainNav = false) {
  return (
    href === item.url || // /endpint?search=param
    href.split('?')[0] === item.url || // endpoint
    !!item?.items?.filter((i) => i.url === href).length || // if child nav is active
    (mainNav &&
      href.split('/')[1] !== '' &&
      href.split('/')[1] === item?.url?.split('/')[1])
  )
}
